import './assign-unit-select.scss';
import '@fortawesome/fontawesome-free/css/all.css';
import React, { ReactNode, useCallback, useEffect, useState } from 'react';
import { useAPI } from '../../../clients/quipli-service/quipli-service-client';
import { DateTime } from 'luxon';
import { AssignableUnit } from '../../../clients/quipli-service/availability/availability';
import { SelectForm } from '../../../components/select-form/select-form';
import { components } from 'react-select';
import { Typography } from '../../../components/typography/typography';
import { Location } from '../../../clients/quipli-service/location/location';
import { Configuration } from '../../../clients/quipli-service/configuration/configuration';
import { ConfirmModal } from '../../../components/modal/confirm-modal';
import Icon from '../../../components/icons/icon';
import { debounce } from 'lodash';
import { FieldLabelProps } from '../../../components/field/field';
import { OrderType } from '../../../clients/quipli-service/order/order';

type AssignUnitSelectProps = FieldLabelProps & {
  locationId: number;
  uniqueId: string;
  productId: number;
  quantity: number;
  startDate: DateTime;
  endDate?: DateTime;
  disabled?: boolean;
  selectedUnitIds?: number[];
  onChange?: (sus: number[]) => void;
  isOptionDisabled?: (au: AssignableUnit) => boolean;
  setRootAttributes?: (o: any) => undefined;
  orderProductType: OrderType;
};

export const AssignUnitSelect = ({
  locationId,
  uniqueId,
  productId,
  quantity,
  startDate,
  endDate,
  disabled = false,
  selectedUnitIds,
  onChange,
  isOptionDisabled,
  setRootAttributes,
  label,
  labelType,
  labelWeight,
  isRequired,
  orderProductType,
}: AssignUnitSelectProps) => {
  const { getAssignableUnits, listLocations, getMultipleConfigurations } = useAPI();
  // React vars
  const [assignableUnits, setAssignableUnits] = useState<AssignableUnit[]>([]);
  const [selectedUnits, setSelectedUnits] = useState<AssignableUnit[]>([]);
  const [internalUnitIds, setInternalUnitIds] = useState<number[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [locations, setLocations] = useState<Location[]>([]);
  const [configurations, setConfigurations] = useState<Configuration[]>([]);
  const [actionModal, setActionModal] = useState<ReactNode>();

  const debouncedSetIsOpen = useCallback(
    debounce(function (state: boolean) {
      setIsOpen(state);
    }, 2),
    [isOpen],
  );
  useEffect(() => {
    listLocations()
      .then(setLocations)
      .catch(() => setLocations([]));
    getMultipleConfigurations([
      'CONF_UNIT_TRACKING_VIA',
      'CONF_ALLOW_MULTI_LOCATION_UNITS',
      'CONF_COMBINE_SERIAL_UNIT_NUMBER',
    ])
      .then((response) => {
        setConfigurations(response);
      })
      .catch(() => setConfigurations([]));
  }, []);

  const unitTrackingField =
    configurations.find((conf) => conf.variable === 'CONF_UNIT_TRACKING_VIA')?.value === '1'
      ? 'serialNumber'
      : 'unitNumber';

  const enableMultiLocationUnits =
    configurations.find((conf) => conf.variable === 'CONF_ALLOW_MULTI_LOCATION_UNITS')?.value === '1';

  const combinedSerialUnitNumber =
    configurations.find((conf) => conf.variable === 'CONF_COMBINE_SERIAL_UNIT_NUMBER')?.value === '1';

  useEffect(() => {
    const sus = assignableUnits
      .filter((au) => (selectedUnitIds || internalUnitIds).indexOf(au.unit.id) >= 0)
      .sort((a, b) => a.unit[unitTrackingField].localeCompare(b.unit[unitTrackingField]));
    setSelectedUnits(sus);
  }, [selectedUnitIds, assignableUnits]);

  async function refresh() {
    // Get all available units
    const aus = await getAssignableUnits(productId, locationId, startDate, endDate, false, [], orderProductType);

    function statusToNumber(
      status: 'Available' | 'Scheduled' | 'Past Due' | 'On Rent' | 'Off Rent' | 'Hard Down' | 'Sold' | 'Not In Service',
    ): number {
      switch (status) {
        case 'Available':
          return 0;
        case 'Scheduled':
          return 1;
        case 'Past Due':
          return 2;
        case 'On Rent':
          return 3;
        case 'Hard Down':
          return 4;
        case 'Off Rent':
          return 5;
        case 'Sold':
          return 6;
        case 'Not In Service':
          return 7;
      }
    }

    setAssignableUnits(
      aus
        .filter((u) => (orderProductType === OrderType.Rental ? u.unit.forRent : u.unit.forSale))
        .sort((a, b) => {
          const statusComp = statusToNumber(a.status) - statusToNumber(b.status);
          if (statusComp !== 0) {
            return statusComp;
          } else {
            return a.unit[unitTrackingField].localeCompare(b.unit[unitTrackingField]);
          }
        }),
    );
  }

  useEffect(() => {
    refresh();
  }, [locationId, disabled, startDate, endDate]);

  useEffect(() => {
    // Update lenght of selected units on this component
    setRootAttributes &&
      setRootAttributes({
        'data-length': selectedUnits.length,
      });
  }, [setRootAttributes, selectedUnits.length]);

  async function handleInputChange(units: AssignableUnit[]) {
    const fn = onChange || setInternalUnitIds;
    if (!enableMultiLocationUnits) {
      fn(units.map((u) => u.unit.id));
    } else {
      const newUnits = units.map((u) => u.unit);
      const alreadySelectedUnits = selectedUnits.map((u) => u.unit);
      const newSelectedUnit = newUnits.find((u) => !alreadySelectedUnits.some((su) => su === u));

      if (newSelectedUnit && newSelectedUnit.locationId !== +locationId) {
        const newSelectedUnitLocationName = locations.find((l) => l.id === newSelectedUnit.locationId)?.name;
        setActionModal(
          <ConfirmModal
            isOpen={true}
            close={() => setActionModal(undefined)}
            onConfirm={async () => {
              fn(units.map((u) => u.unit.id));
              await refresh();
              setActionModal(undefined);
            }}
            yesDataCy="assign-unit-select-yes"
            headerIcon={
              <div className="flex justify-center">
                <Icon icon="InfoCircle" size="xxlarge" stroke="#fcda01" />
              </div>
            }
            prompt="Different Location!"
            additionalText={
              <>
                Unit "{newSelectedUnit[unitTrackingField]}" is in <b>{newSelectedUnitLocationName}</b>.
                <br />
                Are you sure you want to assign a product from a different store?
              </>
            }
            yesButtonYellow={true}
            yesLabel="Assign"
            noLabel="Cancel"
          />,
        );
      } else {
        fn(units.map((u) => u.unit.id));
      }
    }
    if (units.length >= quantity) {
      //give time for on click to fire first
      setTimeout(() => setIsOpen(false), 0);
    }
    await refresh();
  }

  const assignableUnitsByLocation = enableMultiLocationUnits
    ? locations
        .sort((a, b) => {
          if (a.id === locationId) {
            return -1;
          } else if (b.id === locationId) {
            return 1;
          } else {
            return a.name.localeCompare(b.name);
          }
        })
        .map((location) => ({
          label: location.name,
          options: assignableUnits.filter(
            (au) => au.unit.locationId === location.id && !selectedUnits.some((s) => s.unit.id === au.unit.id),
          ),
        }))
        .filter((item) => item.options.length > 0)
    : undefined;
  const assignableUnitOptions = !assignableUnitsByLocation
    ? assignableUnits.filter((au) => !selectedUnits.some((s) => s.unit.id === au.unit.id))
    : assignableUnitsByLocation;

  return (
    <div className="flex flex-column gap-2 text-[0.9rem] text-[var(--grey-500)] w-full">
      <SelectForm
        className="assign-unit-select"
        dataCy="assign-unit-select"
        isMulti
        isRequired={isRequired}
        label={label}
        labelType={labelType}
        labelWeight={labelWeight}
        isSearchable={true}
        closeMenuOnSelect={false}
        menuIsOpen={isOpen}
        menuPortalTarget={document.body}
        menuShouldBlockScroll={true}
        onMenuOpen={() => debouncedSetIsOpen(true)}
        onMenuClose={() => debouncedSetIsOpen(false)}
        name={`new_assign_serial[${uniqueId}][]`}
        value={selectedUnits}
        options={assignableUnitOptions}
        getOptionLabel={(au) => {
          return combinedSerialUnitNumber
            ? `Unit - ${au.unit['unitNumber']} Serial - ${au.unit['serialNumber']}`
            : au.unit[unitTrackingField];
        }}
        getOptionValue={(au) => `${au.unit.id}`}
        isOptionDisabled={(au) => (isOptionDisabled && isOptionDisabled(au)) || selectedUnits.length >= quantity}
        onChange={(units) => {
          handleInputChange(units as AssignableUnit[]);
          if (units.length < selectedUnits.length) {
            !isOpen && setIsOpen(true);
          }
        }}
        components={{
          Option: (props) => (
            <components.Option {...props}>
              <div className="quipli-v2 flex items-center gap-[6px] justify-between">
                <Typography type="text-sm">{props.children}</Typography>
                <div className="assign-unit-select">
                  <div className={`status-badge ${props.data.status.toLowerCase().replaceAll(' ', '-')}`}>
                    <span>{props.data.status}</span>
                  </div>
                </div>
              </div>
            </components.Option>
          ),
        }}
      />
      {quantity >= 5 && (
        <span>
          {selectedUnits.length}/{quantity} assigned
        </span>
      )}
      {actionModal}
    </div>
  );
};
